// iter605 — Inline help drawer for the Printer Settings dialog. Opens
// from a small "?" pill next to the title and explains the three
// things branch managers usually need: (a) how to find a printer's IP,
// (b) what the receipt/kitchen/bar roles do, and (c) how the optional
// Print Bridge enables silent printing.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Wifi, Printer, ChefHat, GlassWater, HelpCircle, Download } from 'lucide-react';

const SECTIONS = [
  {
    id: 'find-ip',
    icon: Wifi,
    title: 'How do I find my printer IP address?',
    body: (
      <div className="space-y-2 text-xs">
        <p>Every network thermal printer has a way to print its current IP. The exact steps depend on the model:</p>
        <ul className="list-disc ml-4 space-y-1">
          <li><strong>Epson TM-m30 / TM-T20 / TM-T88</strong>: hold the <code>Feed</code> button while powering it on — it prints a {'"Status sheet"'} with the IP.</li>
          <li><strong>Star TSP100 / TSP143 / TSP650</strong>: hold the <code>Feed</code> button for 3 seconds with the printer ON — it prints a self-test page with the IP.</li>
          <li><strong>Xprinter / Rongta / generic 80mm</strong>: hold <code>Feed</code> while turning on — self-test with IP at the bottom.</li>
          <li><strong>Still nothing?</strong> Check your Wi-Fi router admin page for {'"connected devices"'} — the printer shows up by its model name.</li>
        </ul>
        <p className="text-muted-foreground">Tip: if your printer is set to DHCP its IP can change. Reserve a fixed IP for it in your router (recommended).</p>
      </div>
    ),
  },
  {
    id: 'roles',
    icon: Printer,
    title: 'What do the printer roles mean?',
    body: (
      <div className="space-y-2 text-xs">
        <div className="flex items-start gap-2"><Printer size={13} className="text-violet-600 mt-0.5 shrink-0" /><div><strong>Cashier / Receipt</strong> — the customer&apos;s full bill (items, prices, tax, total, payment method). Every branch needs one.</div></div>
        <div className="flex items-start gap-2"><ChefHat size={13} className="text-orange-600 mt-0.5 shrink-0" /><div><strong>Kitchen (KOT)</strong> — a tear-off ticket for the chefs. Bigger fonts, NO prices, modifiers indented (e.g. {'"+ no onion"'}). Only food categories print here.</div></div>
        <div className="flex items-start gap-2"><GlassWater size={13} className="text-cyan-600 mt-0.5 shrink-0" /><div><strong>Bar</strong> — same idea as Kitchen but only drinks categories print here. Use it if your branch has a separate bar prep area.</div></div>
        <p className="text-muted-foreground">Assign categories to Kitchen vs Bar in <em>Menu &rarr; Manage Categories &rarr; Print</em>.</p>
      </div>
    ),
  },
  {
    id: 'silent',
    icon: Download,
    title: 'What is the Print Bridge? Do I really need it?',
    body: (
      <div className="space-y-2 text-xs">
        <p><strong>Free</strong>, tiny Python script that runs on your cashier PC. It&apos;s the only way to get TRUE silent printing (no dialog popping up every time the cashier hits Print).</p>
        <ul className="list-disc ml-4 space-y-1">
          <li><strong>Windows / Mac</strong>: download from the green banner at the top of this dialog, double-click to run, leave it open. Install takes ~5 minutes.</li>
          <li><strong>iPad / Android</strong>: the bridge cannot run there (iOS doesn&apos;t allow it). The OS print dialog will appear once — select your AirPrint printer once, iOS remembers, all subsequent prints are one tap.</li>
        </ul>
        <p>Even without the bridge, printing still works — you just get a one-tap dialog instead of true silent.</p>
      </div>
    ),
  },
  {
    id: 'ipad-airprint',
    icon: Download,
    title: 'iPad / iPhone setup — free Epson & Star utilities',
    body: (
      <div className="space-y-3 text-xs">
        <p>iOS doesn&apos;t accept driver downloads — it uses <strong>AirPrint</strong>. Most thermal printers expose AirPrint automatically once they&apos;re on the same Wi-Fi as the iPad. Some older models need a one-time configuration via the manufacturer&apos;s free iOS utility:</p>
        <div className="rounded-lg border bg-white p-2 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold">Epson TM Utility <span className="text-muted-foreground font-normal">(free)</span></div>
              <div className="text-[11px] text-muted-foreground">Configure / test Epson TM-T20III, TM-m30II, TM-T88VI, TM-T82III. Set Wi-Fi, IP, AirPrint on/off.</div>
            </div>
            <a href="https://apps.apple.com/app/epson-tm-utility/id663047879" target="_blank" rel="noreferrer"
               className="shrink-0 text-[11px] font-semibold text-indigo-700 hover:underline" data-testid="link-epson-tm-utility">
              App Store ↗
            </a>
          </div>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold">Epson TM Print Assistant <span className="text-muted-foreground font-normal">(free)</span></div>
              <div className="text-[11px] text-muted-foreground">One-tap test print for Epson thermal printers. Confirms LAN visibility before opening Smarto POS.</div>
            </div>
            <a href="https://apps.apple.com/app/epson-tm-print-assistant/id1071346767" target="_blank" rel="noreferrer"
               className="shrink-0 text-[11px] font-semibold text-indigo-700 hover:underline" data-testid="link-epson-tm-assistant">
              App Store ↗
            </a>
          </div>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold">Star Quick Setup Utility <span className="text-muted-foreground font-normal">(free)</span></div>
              <div className="text-[11px] text-muted-foreground">For Star TSP100 / TSP143 / mC-Print3. Same idea: configure Wi-Fi & AirPrint from your iPad.</div>
            </div>
            <a href="https://apps.apple.com/app/star-quick-setup-utility/id1186403709" target="_blank" rel="noreferrer"
               className="shrink-0 text-[11px] font-semibold text-indigo-700 hover:underline" data-testid="link-star-quick-setup">
              App Store ↗
            </a>
          </div>
        </div>
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-2 text-[11px]">
          <strong className="text-amber-900">Note for Epson TM-T20III owners:</strong> the TM-T20III is Ethernet-only and does <strong>not</strong> support AirPrint natively. Two options on iPad:
          <ol className="list-decimal ml-4 mt-1 space-y-0.5">
            <li>Install the Epson TM Utility above and pair it via Wi-Fi (works for the Wi-Fi variant only).</li>
            <li>Or run the Smarto Print Bridge on a Mac/PC on the same network — iPad sends the receipt to the bridge, bridge talks ESC/POS to the printer. (Recommended for kiosk setups.)</li>
          </ol>
        </div>
      </div>
    ),
  },
  {
    id: 'flow',
    icon: ChefHat,
    title: 'How does an order get printed?',
    body: (
      <div className="space-y-2 text-xs">
        <ol className="list-decimal ml-4 space-y-1">
          <li>Cashier taps <strong>Pay</strong>. POS picks the default receipt printer for the current branch — prints the full bill.</li>
          <li>If a kitchen printer is configured, POS auto-fires a <strong>kitchen ticket</strong> with only the food items.</li>
          <li>If a bar printer is configured, drinks categories fire there too — in parallel.</li>
          <li>Each printer&apos;s status (online/offline) shows on the <strong>dashboard</strong> in the {'"Branch Printers"'} tile.</li>
        </ol>
      </div>
    ),
  },
];

export default function PrinterSettingsHelpPanel() {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState('find-ip');

  return (
    <div className="rounded-xl border bg-slate-50/70 overflow-hidden" data-testid="printer-help-panel">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="w-full justify-between rounded-none h-9 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100"
        data-testid="printer-help-toggle"
      >
        <span className="flex items-center gap-2">
          <HelpCircle size={13} className="text-info" />
          Need help setting this up?
        </span>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </Button>
      {open && (
        <div className="px-3 pb-3 space-y-1" data-testid="printer-help-content">
          <a href="/printer-setup" target="_blank" rel="noreferrer"
             className="block px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 transition flex items-center justify-between"
             data-testid="printer-help-open-full-guide">
            <span>📖 Open the full Printer Setup guide</span>
            <span>↗</span>
          </a>
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isOpen = expandedId === s.id;
            return (
              <div key={s.id} className="rounded-lg bg-white border">
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : s.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold hover:bg-slate-50 rounded-lg"
                  data-testid={`printer-help-section-${s.id}`}
                >
                  <span className="flex items-center gap-2">
                    <Icon size={13} className="text-info" />
                    {s.title}
                  </span>
                  {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {isOpen && <div className="px-3 pb-3 pt-1 border-t border-slate-100">{s.body}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
